/**
 * THE AREA QUESTION, as data: what a tick list needs to be drawn, here or
 * on the GM's screen.
 *
 * Built once by the attacker's client from the armed weapon and the roster,
 * and (for a player) sent to the GM inside an ask, so the GM's screen shows
 * the same list without holding the attacker's weapon. It names:
 *
 * - what is being fired and its declared shape, as a GUIDE in words: the
 *   table judges who is inside three metres, the app only says three metres;
 * - who MAY be ticked: everyone on the roster the weapon's own `catches`
 *   rule allows, never the attacker - except when a blast has scattered and
 *   the table's ruling lets it fall on its thrower;
 * - the same for the secondary template, when the weapon has one;
 * - the proposal so far.
 *
 * Pure, zero React dependency.
 */
import { areaOf, sizeExpression } from '../areaShapes';
import type { AreaCatches, AreaSize, SpawnedArea, WeaponArea } from '../areaShapes';
import { blastCatchesAttacker } from '../blastRules';
import type { BlastSelfPolicy } from '../blastRules';
import { effectReaches } from '../effectScope';
import { scatterNote } from '../scatter';
import type { ScatterResult } from '../scatter';
import { teamOf } from '../roster/team';
import type { Combatant } from '../roster/types';
import { readTicks, ticksFor } from './areaTicks';
import type { AreaTicks } from './areaTicks';

interface TickCandidate {
  id: string;
  name: string;
}

export interface AreaAsk {
  attacker: string;
  weapon: string;
  /** The primary's shape, or 'suppression' for Suppressing Fire's arc. */
  shape: WeaponArea['shape'] | 'suppression';
  /** The declared size, in words - a guide for whoever ticks. */
  guide: string;
  /** Where a missed blast went, in the log's words; null for an aimed shot. */
  scatter: string | null;
  candidates: TickCandidate[];
  /** The secondary template, when the weapon's victims set one off. */
  spawn: { guide: string; candidates: TickCandidate[] } | null;
  ticks: AreaTicks;
}

type AreaSource = Parameters<typeof areaOf>[0];

/** Is this shot an area one by DECLARATION - so the question has to be asked before it is fired? */
export function needsTicks(slot: AreaSource, suppressing: boolean): boolean {
  return suppressing || areaOf(slot).shape !== 'none';
}

/** A size as written on the weapon: its dice when it has them, else its number; null for "not set". */
function sizeText(size: AreaSize): string | null {
  const dice = sizeExpression(size);
  if (dice) return dice;
  return Number(size) > 0 ? String(size) : null;
}

const GUIDES: Record<Exclude<WeaponArea['shape'], 'none'>, (block: WeaponArea | SpawnedArea) => string> = {
  blast: (b) => `Blast, ${sizeText(b.radius) ?? '?'} m radius`,
  cone: (b) =>
    `Cone, ${sizeText(b.angle) ?? '?'} degrees` + (sizeText(b.length) ? `, ${sizeText(b.length)} m long` : ''),
  beam: (b) => 'Beam' + (sizeText(b.length) ? `, ${sizeText(b.length)} m long` : ", to the weapon's range"),
  arc: (b) => `Arc, ${sizeText(b.leaps) ?? '?'} leaps of ${sizeText(b.reach) ?? '?'} m, in the order you tick`,
};

/** A template's declared size in words. */
function guideOf(block: WeaponArea | SpawnedArea): string {
  return block.shape === 'none' ? '' : GUIDES[block.shape](block);
}

interface Who {
  roster: readonly Combatant[];
  actor: Combatant | null;
}

/** Everyone the template's `catches` rule allows, in roster order; the attacker only when `withAttacker`. */
function candidatesFor({ roster, actor }: Who, catches: AreaCatches, aimedId: string | null, withAttacker: boolean) {
  const attackerTeam = teamOf(actor)?.tag ?? null;
  return roster
    .filter((c) => c.id !== actor?.id || withAttacker)
    .filter((c) => c.id === aimedId || effectReaches(catches, attackerTeam, teamOf(c)?.tag ?? null))
    .map((c) => ({ id: c.id, name: c.name }));
}

interface AskInput extends Who {
  slot: (AreaSource & { name?: string; weaponType?: string }) | null;
  suppressing: boolean;
  /** Who the shot was aimed at. Ticked to begin with, unless it scattered. */
  aimedId: string | null;
  scatter: ScatterResult | null;
  blastSelfPolicy: BlastSelfPolicy;
}

/** What the shot is, apart from who is in it: its shape, its guide, its rule about whom it catches, its secondary. */
function shotOf(slot: AskInput['slot'], suppressing: boolean) {
  const block = areaOf(slot);
  if (suppressing) {
    const guide = "Suppressing Fire: everyone in a 45 degree arc, out to the weapon's range";
    return { block, shape: 'suppression' as const, guide, catches: 'anyone' as AreaCatches, spawn: null };
  }
  return { block, shape: block.shape, guide: guideOf(block), catches: block.catches, spawn: block.spawn ?? null };
}

export function areaAskOf(input: AskInput): AreaAsk {
  const { slot, aimedId, scatter, actor } = input;
  const shot = shotOf(slot, input.suppressing);
  // The thrower may be caught only by a ranged blast, and only as the table rules (engine/blastRules).
  const rangedBlast = slot?.weaponType !== 'melee' && shot.block.shape === 'blast';
  const withAttacker = rangedBlast && blastCatchesAttacker(input.blastSelfPolicy, !!scatter);
  // A scattered blast was aimed at nobody: nobody is exempt from its rule, and nobody starts ticked.
  const aimed = scatter ? null : aimedId;
  return {
    attacker: actor?.name ?? 'Someone',
    weapon: slot?.name ?? 'weapon',
    shape: shot.shape,
    guide: shot.guide,
    scatter: scatter ? scatterNote(scatter) : null,
    candidates: candidatesFor(input, shot.catches, aimed, withAttacker),
    spawn: shot.spawn && {
      guide: guideOf(shot.spawn),
      candidates: candidatesFor(input, shot.spawn.catches, null, false),
    },
    ticks: ticksFor(aimed),
  };
}

const text = (value: unknown, fallback: string): string => (typeof value === 'string' && value ? value : fallback);

function readCandidates(raw: unknown): TickCandidate[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is { id: string; name?: unknown } => typeof c?.id === 'string' && c.id !== '')
    .map((c) => ({ id: c.id, name: text(c.name, c.id) }));
}

const SHAPES: AreaAsk['shape'][] = ['blast', 'cone', 'beam', 'arc', 'suppression', 'none'];

/**
 * An area question that crossed the wire, validated field by field. The GM's
 * screen draws whatever a player's client sent, so nothing in it is trusted
 * to be the right type; what cannot be read takes a harmless default.
 */
export function readAreaAsk(raw: unknown): AreaAsk {
  const stored = (raw ?? {}) as Record<string, unknown>;
  const spawn = stored.spawn as Record<string, unknown> | null | undefined;
  return {
    attacker: text(stored.attacker, 'Someone'),
    weapon: text(stored.weapon, 'weapon'),
    shape: SHAPES.find((shape) => shape === stored.shape) ?? 'blast',
    guide: text(stored.guide, ''),
    scatter: typeof stored.scatter === 'string' ? stored.scatter : null,
    candidates: readCandidates(stored.candidates),
    spawn: spawn ? { guide: text(spawn.guide, ''), candidates: readCandidates(spawn.candidates) } : null,
    ticks: readTicks(stored.ticks),
  };
}
